import { useEffect, useRef, useState } from 'react';
import { select, scaleLinear, curveMonotoneX, line as d3Line } from 'd3';
import { Button, Form, Collapse, Modal, OverlayTrigger, Popover } from 'react-bootstrap';
import { OffcanvasWrapper } from '@/frontend/components/OffcanvasWrapper';
import { I18n } from '@/i18n/I18n';
import { ParameterInputType, ParameterSubjectType, ParameterType, UpdatedParamsAndRefs } from '@/types/parameterTypes';
import { CUSTOM_FIELD_TYPES, CustomFieldParams } from '@/types/customFieldTypes';
import { useShallow } from 'zustand/react/shallow';
import { useParameterStore } from '@/frontend/store/ParameterStore';
import { useTreeStore } from '@/frontend/store/TreeStore';
import { useGlobalStore } from '@/frontend/store/GlobalStore';
import { parseCsvData, renderAxes, renderDataLine, renderLoadingState, renderNoDataState, renderSliders, renderXAxisSliders, renderTitle, updateParameterValue, getSliderValueFromField, calculateBaseDomains, applyZoomAndPan, countCsvDataPoints, getDataExtent, filterDataToDomain, downsampleToMaxPoints, findMaxBetweenLimits } from './graphUtils';
import { DataPoint, SliderData, GraphItemProps, SliderType } from './types';
import { FileInputSection, GraphCaptureOutputSection } from './FileInputSection';
import { SliderControl } from './SliderControl';
import { ZoomPanControls } from './ZoomPanControls';
import { FileSubjectTypes } from '@/types/fileTypes';
import { GRAPH_MARGIN, MAX_DISPLAYED_POINTS } from './constants';
import { SidePanelIcon } from '@/frontend/SVGs/SidePanelIcon';
import { FileRunView } from '@/frontend/components/files/FileRunView';
import { BasicButton } from '../../buttons/BasicButton';

export function GraphItem(props: GraphItemProps) {
  const { operation, runView } = props;
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [sliders, setSliders] = useState<SliderData[]>([]);
  const [nextSliderId, setNextSliderId] = useState<number>(1);
  const [dimensions, setDimensions] = useState({ width: 800, height: 500 });
  const [fullCsvData, setFullCsvData] = useState<DataPoint[]>([]);
  const [originalDataPointCount, setOriginalDataPointCount] = useState(0);
  const [displayedDataPointCount, setDisplayedDataPointCount] = useState(0);
  const [downsampleRatio, setDownsampleRatio] = useState<number | null>(null);
  const [isLoadingCsv, setIsLoadingCsv] = useState<boolean>(false);
  const [csvError, setCsvError] = useState<string | null>(null);
  const [previousCsvFileId, setPreviousCsvFileId] = useState<string | undefined>(undefined);

  const parameterFileInput = useParameterStore(useShallow(state => state.parametersForSubjectAndType(operation.id, ParameterType.FIELD)[0]));
  const parameterFileCapture = useParameterStore(useShallow(state => state.parametersForSubjectAndType(operation.id, ParameterType.OUTPUT)[0]));
  const slidersCaptured = parameterFileCapture?.fields?.[0]?.file;
  const getFileSourceUrl = useGlobalStore(state => state.getFileSourceUrl);
  const createFile = useGlobalStore(state => state.createFile);
  const deleteFile = useGlobalStore(state => state.deleteFile);
  const handleUpdatedParamsAndRefs = useParameterStore(state => state.handleUpdatedParamsAndRefs);
  const { createParameter, updateCustomField, deleteParameter } = useParameterStore();
  const updateGraph = useTreeStore(state => state.updateGraph);
  const [xAxisLabel, setXAxisLabel] = useState(operation.graph?.xAxisLabel || I18n.get('graphXAxis'));
  const [yAxisLabel, setYAxisLabel] = useState(operation.graph?.yAxisLabel || I18n.get('graphYAxis'));
  const [xZoom, setXZoom] = useState(1.0);
  const [xPan, setXPan] = useState(0);
  const [yZoom, setYZoom] = useState(1.0);
  const [yPan, setYPan] = useState(0);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [showAxisLabels, setShowAxisLabels] = useState(true);
  const [showSliders, setShowSliders] = useState(true);
  const [showResetSlidersModal, setShowResetSlidersModal] = useState(false);

  const fileInputField = parameterFileInput?.fields[0];
  const fileId = fileInputField?.file?.id;

  const yAxisParams = useParameterStore(useShallow(state =>
    state.parametersForSubject(operation.id, ParameterSubjectType.OPERATION)
      .filter(p => p.type === ParameterType.YSLIDER)
  ));

  const xAxisParams = useParameterStore(useShallow(state =>
    state.parametersForSubject(operation.id, ParameterSubjectType.OPERATION)
      .filter(p => p.type === ParameterType.XSLIDER)
  ));

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const containerHeight = Math.max(400, window.innerHeight * 0.4);
        setDimensions({
          width: Math.max(600, containerWidth - 20),
          height: containerHeight
        });
      }
    };

    updateDimensions();

    const resizeObserver = new ResizeObserver(updateDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener('resize', updateDimensions);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  useEffect(() => {
    const loadCsvFromParameter = async () => {
      if (fileId) {
        try {
          setIsLoadingCsv(true);
          const response = await getFileSourceUrl(fileId);

          if (response.status === 200) {
            const csvResponse = await fetch(response.downloadUrl);
            if (!csvResponse.ok) {
              setCsvError(I18n.get('graphErrorLoadingCsv'));
              setFullCsvData([]);
              setOriginalDataPointCount(0);
            } else {
              const content = await csvResponse.text();
              setCsvError(null);
              const originalCount = countCsvDataPoints(content);
              setOriginalDataPointCount(originalCount);
              const parsedData = parseCsvData(content, 1);
              setFullCsvData(parsedData);
            }
          } else {
            setCsvError(I18n.get('graphErrorLoadingCsv'));
            setFullCsvData([]);
          }
        } catch (error) {
          console.error('Error loading CSV from parameter:', error);
          setCsvError(I18n.get('graphErrorLoadingCsv'));
          setFullCsvData([]);
        } finally {
          setIsLoadingCsv(false);
        }
      } else {
        setCsvError(null);
        setFullCsvData([]);
        setIsLoadingCsv(false);
      }
    };

    void loadCsvFromParameter();
  }, [fileId, getFileSourceUrl]);

  useEffect(() => {
    const currentCsvFileId = fileId;

    if (previousCsvFileId && !currentCsvFileId && parameterFileCapture?.fields?.[0]?.file?.id) {
      const captureFileId = parameterFileCapture.fields[0].file.id;
      void deleteFile(captureFileId, handleUpdatedParamsAndRefs);
    }

    setPreviousCsvFileId(currentCsvFileId);
  }, [fileId, parameterFileCapture?.fields?.[0]?.file?.id, deleteFile, handleUpdatedParamsAndRefs, previousCsvFileId]);

  useEffect(() => {
    if (operation.graph) {
      setXAxisLabel(operation.graph.xAxisLabel || I18n.get('graphXAxis'));
      setYAxisLabel(operation.graph.yAxisLabel || I18n.get('graphYAxis'));
    }
  }, [operation.graph]);

  useEffect(() => {
    const ySliderData: SliderData[] = yAxisParams.map((param, index) => {
      const field = param.fields[0];
      const value = getSliderValueFromField(field);

      return {
        id: param.id,
        name: param.title || `Y Slider ${index + 1}`,
        value,
        parameter: param,
        type: SliderType.Y
      };
    });

    const xSliderData: SliderData[] = xAxisParams.map((param, index) => {
      const field = param.fields[0];
      const value = getSliderValueFromField(field);

      return {
        id: param.id,
        name: param.title || `X Slider ${index + 1}`,
        value,
        parameter: param,
        type: SliderType.X
      };
    });

    const allSliderData = [...ySliderData, ...xSliderData];

    setSliders(prevSliders => {
      if (prevSliders.length !== allSliderData.length) {
        const maxYSlider = ySliderData.length;
        const maxXSlider = xSliderData.length;
        setNextSliderId(Math.max(maxYSlider, maxXSlider) + 1);
        return allSliderData;
      }

      const mergedSliders = allSliderData.map(newSlider => {
        const existingSlider = prevSliders.find(s => s.id === newSlider.id);
        if (existingSlider) {
          return {
            ...existingSlider,
            parameter: newSlider.parameter,
            name: newSlider.name !== existingSlider.name ? newSlider.name : existingSlider.name,
            value: newSlider.value !== existingSlider.value ? newSlider.value : existingSlider.value,
            type: newSlider.type
          };
        }
        return newSlider;
      });

      const maxYSlider = ySliderData.length;
      const maxXSlider = xSliderData.length;
      setNextSliderId(Math.max(maxYSlider, maxXSlider) + 1);
      return mergedSliders;
    });
  }, [yAxisParams, xAxisParams]);

  const handleCreateYAxisSlider = async () => {
    const sliderName = `Y Slider ${nextSliderId}`;
    await createParameter({
      title: sliderName,
      fieldType: CUSTOM_FIELD_TYPES.Float,
      position: 0,
      subjectId: operation.id,
      subjectType: ParameterSubjectType.OPERATION,
      type: ParameterType.YSLIDER,
      inputType: ParameterInputType.FREE_FORM
    });
  };

  const handleCreateXAxisSlider = async () => {
    const sliderName = `X Slider ${nextSliderId}`;
    await createParameter({
      title: sliderName,
      fieldType: CUSTOM_FIELD_TYPES.Float,
      position: 0,
      subjectId: operation.id,
      subjectType: ParameterSubjectType.OPERATION,
      type: ParameterType.XSLIDER,
      inputType: ParameterInputType.FREE_FORM
    });
  };

  const handleSliderChange = async (sliderId: string, value: number) => {
    setSliders(prev => prev.map(slider =>
      slider.id === sliderId ? { ...slider, value } : slider
    ));

    const slider = sliders.find(s => s.id === sliderId);
    if (slider) {
      await updateParameterValue(slider, value, (request: CustomFieldParams) => updateCustomField(request));
    }
  };

  const xLimitSliders = sliders.filter(s => s.type === SliderType.X);
  const canAutoMax = fullCsvData.length > 0 && xLimitSliders.length >= 2;

  const handleAutoMaxSlider = async (sliderId: string) => {
    if (!canAutoMax) {
      return;
    }
    const xValues = xLimitSliders.map(s => s.value);
    const maxPoint = findMaxBetweenLimits(fullCsvData, Math.min(...xValues), Math.max(...xValues));
    if (maxPoint) {
      await handleSliderChange(sliderId, maxPoint.y);
    }
  };

  const handleDeleteSlider = async (sliderId: string) => {
    try {
      const sliderToDelete = sliders.find(slider => slider.id === sliderId);
      if (sliderToDelete?.parameter) {
        await deleteParameter({ id: sliderToDelete.parameter.id });
      }
    } catch (error) {
      console.error('Failed to delete slider parameter:', error);
    }
  };

  const handleResetView = () => {
    setXZoom(1.0);
    setXPan(0);
    setYZoom(1.0);
    setYPan(0);
  };

  const handleConfirmSliderLocations = async () => {
    if (!svgRef.current) {
      return;
    }

    try {
      const svgElement = svgRef.current;

      let svgWidth: number;
      if (svgElement.clientWidth > 0) {
        svgWidth = svgElement.clientWidth;
      } else {
        const widthAttr = svgElement.getAttribute('width');
        svgWidth = widthAttr ? parseFloat(widthAttr) : dimensions.width;
      }

      let svgHeight: number;
      if (svgElement.clientHeight > 0) {
        svgHeight = svgElement.clientHeight;
      } else {
        const heightAttr = svgElement.getAttribute('height');
        svgHeight = heightAttr ? parseFloat(heightAttr) : dimensions.height;
      }

      const clonedSvg = svgElement.cloneNode(true) as SVGSVGElement;
      clonedSvg.setAttribute('width', String(svgWidth));
      clonedSvg.setAttribute('height', String(svgHeight));
      clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

      const svgData = new XMLSerializer().serializeToString(clonedSvg);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);

      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        console.error('Could not get canvas context');
        URL.revokeObjectURL(svgUrl);
        return;
      }

      img.onload = () => {
        canvas.width = svgWidth;
        canvas.height = svgHeight;

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, svgWidth, svgHeight);

        ctx.drawImage(img, 0, 0, svgWidth, svgHeight);

        canvas.toBlob((blob) => {
          if (!blob) {
            console.error('Failed to create blob from canvas');
            URL.revokeObjectURL(svgUrl);
            return;
          }

          const customField = parameterFileCapture?.fields[0];

          if (!customField || !customField.id) {
            console.error('Custom field ID not found');
            URL.revokeObjectURL(svgUrl);
            return;
          }

          const reader = new FileReader();
          reader.onloadend = async () => {
            const existingFile = customField.file;
            if (existingFile?.id) {
              try {
                await deleteFile(existingFile.id, () => {});
              } catch (error: unknown) {
                console.error('Error deleting old file:', error);
                URL.revokeObjectURL(svgUrl);
                return;
              }
            }

            const file = new File([blob], 'graph-capture.png', { type: 'image/png' });
            await createFile(
              { fileName: 'graph-capture.png', subjectId: customField.id, subjectType: FileSubjectTypes.CUSTOM_FIELD },
              file,
              handleUpdatedParamsAndRefs,
              () => {}
            );

            URL.revokeObjectURL(svgUrl);
          };

          reader.onerror = () => {
            console.error('Error reading blob');
            URL.revokeObjectURL(svgUrl);
          };

          reader.readAsDataURL(blob);
        }, 'image/png');
      };

      img.onerror = () => {
        console.error('Failed to load SVG image');
        URL.revokeObjectURL(svgUrl);
      };

      img.src = svgUrl;
    } catch (error) {
      console.error('Error converting SVG to PNG:', error);
    }
  };

  useEffect(() => {
    if (!svgRef.current) return;

    select(svgRef.current).selectAll('*').remove();

    const width = dimensions.width - GRAPH_MARGIN.left - GRAPH_MARGIN.right;
    const height = dimensions.height - GRAPH_MARGIN.top - GRAPH_MARGIN.bottom;

    const svg = select(svgRef.current)
      .attr('width', width + GRAPH_MARGIN.left + GRAPH_MARGIN.right)
      .attr('height', height + GRAPH_MARGIN.top + GRAPH_MARGIN.bottom);

    const g = svg.append('g')
      .attr('transform', `translate(${GRAPH_MARGIN.left},${GRAPH_MARGIN.top})`);

    if (isLoadingCsv) {
      renderLoadingState(g, width, height);
      return;
    }

    if (fullCsvData.length === 0) {
      renderNoDataState(g, width, height);
      return;
    }

    const plotWidth = width - 50;

    const baseDomains = calculateBaseDomains(fullCsvData, sliders);
    const dataExtent = getDataExtent(fullCsvData);

    const xDomain = applyZoomAndPan(
      baseDomains.xBaseMin,
      baseDomains.xBaseMax,
      baseDomains.xBaseRange,
      xZoom,
      xPan,
      undefined,
      undefined,
      dataExtent.xMin,
      dataExtent.xMax
    );

    const yDomain = applyZoomAndPan(
      baseDomains.yBaseMin,
      baseDomains.yBaseMax,
      baseDomains.yBaseRange,
      yZoom,
      yPan,
      undefined,
      true,
      dataExtent.yMin,
      dataExtent.yMax
    );

    const filteredData = filterDataToDomain(fullCsvData, xDomain, yDomain);
    const filteredCount = filteredData.length;
    const dataToUse = downsampleToMaxPoints(filteredData, MAX_DISPLAYED_POINTS);
    const displayedCount = dataToUse.length;
    setDisplayedDataPointCount(displayedCount);
    setDownsampleRatio(filteredCount > MAX_DISPLAYED_POINTS ? filteredCount / displayedCount : null);

    const xScale = scaleLinear()
      .domain([xDomain.min, xDomain.max])
      .range([0, plotWidth]);

    const yScale = scaleLinear()
      .domain([yDomain.min, yDomain.max])
      .range([height, 0]);

    const line = d3Line<DataPoint>()
      .x(d => xScale(d.x))
      .y(d => yScale(d.y))
      .curve(curveMonotoneX);

    const currentXAxisLabel = operation.graph?.xAxisLabel || xAxisLabel || I18n.get('graphXAxis');
    const currentYAxisLabel = operation.graph?.yAxisLabel || yAxisLabel || I18n.get('graphYAxis');
    renderAxes(g, xScale, yScale, plotWidth, height, currentXAxisLabel, currentYAxisLabel);
    renderDataLine(g, dataToUse, line);
    renderTitle(g, plotWidth, operation.title || I18n.get('graphTitle'));

    const ySliders = sliders.filter(s => s.type === SliderType.Y);
    const xSliders = sliders.filter(s => s.type === SliderType.X);
    renderSliders(g, ySliders, yScale, plotWidth, width, height, (sliderId, value) => void handleSliderChange(sliderId, value), !!slidersCaptured);
    renderXAxisSliders(g, xSliders, xScale, plotWidth, width, height, (sliderId, value) => void handleSliderChange(sliderId, value), ySliders.length, !!slidersCaptured);
  }, [sliders, dimensions, fullCsvData, isLoadingCsv, operation.graph, operation.title, xZoom, xPan, yZoom, yPan, xAxisLabel, yAxisLabel, slidersCaptured]);

  const onConfirmResetSliderLocations = async (slidersCapturedId: string) => {
    setShowResetSlidersModal(false);
    await deleteFile(slidersCapturedId, (response: UpdatedParamsAndRefs) => handleUpdatedParamsAndRefs(response));
  };

  return (
    <div className="w-100">
      <div className="d-flex mb-2">
        <Button
          variant="light btn-light opacity-50"
          size="sm"
          type="button"
          onClick={() => setShowOffcanvas(!showOffcanvas)} aria-label={I18n.get('graphSettings')} title={I18n.get('viewGraphOptions')}>
          <SidePanelIcon />
        </Button>
      </div>
      <FileInputSection runView={runView} operation={operation} parameter={parameterFileInput} />

      {csvError && (
        <div className="alert alert-danger mb-2">
          {csvError}
        </div>
      )}

      {fullCsvData.length > 0 && !isLoadingCsv && (
        <ZoomPanControls
          xZoom={xZoom}
          xPan={xPan}
          yZoom={yZoom}
          yPan={yPan}
          onXZoomChange={setXZoom}
          onXPanChange={setXPan}
          onYZoomChange={setYZoom}
          onYPanChange={setYPan}
          onReset={handleResetView}
        />
      )}

      <div className="d-flex">
        <div
          className={`flex-fill ${slidersCaptured ? 'border border-success-subtle rounded' : ''}`}
          ref={containerRef}
        >
          <svg ref={svgRef} className="w-100" />
        </div>
      </div>

      {operation.graph && fullCsvData.length > 0 && !isLoadingCsv && (
        <div
          className="mb-2 mt-2 p-2 border rounded bg-light"
          role="status"
          aria-label={
            downsampleRatio != null
              ? I18n.get('graphSamplingAriaLabelDownsampled', {
                  csvCount: originalDataPointCount.toLocaleString(),
                  displayedCount: displayedDataPointCount.toLocaleString(),
                  ratio: downsampleRatio.toFixed(2)
                })
              : I18n.get('graphSamplingAriaLabel', {
                  csvCount: originalDataPointCount.toLocaleString(),
                  displayedCount: displayedDataPointCount.toLocaleString()
                })
          }
        >
          <div className="d-flex flex-wrap align-items-center gap-3">
            <span className="text-muted small">
              {I18n.get('graphSamplingCsvPoints', { count: originalDataPointCount.toLocaleString() })}
              {' · '}
              {I18n.get('graphSamplingDisplayed', { count: displayedDataPointCount.toLocaleString() })}
              {downsampleRatio != null && (
                <>
                  {' · '}
                  {I18n.get('graphSamplingDownsampleRatio', { ratio: downsampleRatio.toFixed(2) })}
                </>
              )}
            </span>
            <OverlayTrigger
              placement="top"
              overlay={
                <Popover>
                  <Popover.Header as="h3" className="small">
                    {I18n.get('graphDownsampleHelpTitle')}
                  </Popover.Header>
                  <Popover.Body className="small">
                    {I18n.get('graphDownsampleHelpBody')}
                  </Popover.Body>
                </Popover>
              }
            >
              <button
                type="button"
                className="btn btn-link btn-sm p-0 ms-1 align-middle text-muted border-0"
                aria-label={I18n.get('graphDownsampleHelpTitle')}
              >
                ?
              </button>
            </OverlayTrigger>
          </div>
        </div>
      )}

      <div className="d-flex align-items-center gap-2 mb-2 mt-2">
        { !slidersCaptured &&
          <Button
            variant="btn btn-success"
            size="sm"
            onClick={() => void handleConfirmSliderLocations()}
            disabled={!!slidersCaptured || fullCsvData.length === 0 || isLoadingCsv}
            style={slidersCaptured ? { pointerEvents: 'none' } : {}}
          >
            {I18n.get('confirmSliderLocations')}
          </Button>
        }
        { slidersCaptured &&
          <>
            <span className="d-inline-block">
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setShowResetSlidersModal(true)}
                >
                  {I18n.get('resetSliderLocations')}
                </Button>
                { showResetSlidersModal &&
                  <Modal show onHide={() => setShowResetSlidersModal(false)} centered>
                    <Modal.Header className="bg-danger text-white" closeButton>
                      <Modal.Title>{I18n.get('resetSliderLocations')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="d-flex flex-column">
                      <p className='alert alert-warning'>{I18n.get('deleteImageToRecapture')}</p>
                      <div className="d-flex justify-content-end gap-2">
                        <BasicButton className="btn btn-secondary" onClick={() => setShowResetSlidersModal(false)} label={I18n.get('cancel')} />
                        <BasicButton title={I18n.get('confirmAction', {action: I18n.get('resetSliderLocations')})} className="btn btn-primary" onClick={() => onConfirmResetSliderLocations(slidersCaptured.id)} label={I18n.get('resetSliderLocations')} />
                      </div>
                    </Modal.Body>
                  </Modal>
                }
              </span>
            <FileRunView customField={parameterFileCapture.fields[0]} hideFilePreview />
          </>
        }
      </div>
      <GraphCaptureOutputSection runView={runView} operation={operation} parameter={parameterFileCapture} />
      
      <OffcanvasWrapper
        show={showOffcanvas}
        onHide={() => setShowOffcanvas(false)}
        id="graphSettingsOffcanvas"
        label={I18n.get('graphSettings')}
      >
          {!runView && operation.graph && (
            <div className="mb-4 pb-3 border-bottom">
              <Button
                variant="link"
                className="p-0 text-decoration-none d-flex align-items-center mb-3 text-muted"
                onClick={() => setShowAxisLabels(!showAxisLabels)}
                aria-expanded={showAxisLabels}
                aria-controls="axis-labels-collapse"
              >
                {showAxisLabels ? '▼' : '▶'}
                <h6 className="mb-0 text-muted ms-2">{I18n.get('graphAxisLabels')}</h6>
              </Button>
              <Collapse in={showAxisLabels}>
                <div id="axis-labels-collapse">
                  <Form.Group className="mb-2">
                    <Form.Label className='form-label col-form-label-sm'>{I18n.get('graphXAxis')}</Form.Label>
                    <Form.Control
                      type="text"
                      size='sm'
                      value={xAxisLabel}
                      onChange={(e) => setXAxisLabel(e.target.value)}
                      onBlur={(e) => { if (operation.graph) void updateGraph({ ...operation.graph, xAxisLabel: e.target.value }); }}
                      placeholder={I18n.get('graphXAxis')}
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label className='form-label col-form-label-sm'>{I18n.get('graphYAxis')}</Form.Label>
                    <Form.Control
                      type="text"
                      size='sm'
                      value={yAxisLabel}
                      onChange={(e) => setYAxisLabel(e.target.value)}
                      onBlur={(e) => { if (operation.graph) void updateGraph({ ...operation.graph, yAxisLabel: e.target.value }); }}
                      placeholder={I18n.get('graphYAxis')}
                    />
                  </Form.Group>
                </div>
              </Collapse>
            </div>
          )}

          <div className="mb-4 pb-3 border-bottom">
            <Button
              variant="link"
              className="p-0 text-decoration-none d-flex align-items-center mb-3 text-muted"
              onClick={() => setShowSliders(!showSliders)}
              aria-expanded={showSliders}
              aria-controls="sliders-collapse"
            >
              {showSliders ? '▼' : '▶'}
              <h6 className="mb-0 text-muted ms-2">{I18n.get('graphSliders')}</h6>
            </Button>
            <Collapse in={showSliders}>
              <div id="sliders-collapse">
                <div className="d-flex justify-content-start gap-2 mb-3">
                  {!runView && (
                    <>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => void handleCreateYAxisSlider()}
                        disabled={!!slidersCaptured}
                      >
                        {I18n.get('addYAxisSlider')}
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => void handleCreateXAxisSlider()}
                        disabled={!!slidersCaptured}
                      >
                        {I18n.get('addXAxisSlider')}
                      </Button>
                    </>
                  )}
                </div>

                {sliders.length === 0 ? (
                  <p className="text-muted small mb-0">{I18n.get('graphNoSliders')}</p>
                ) : (
                  <div>
                    {sliders.map((slider, index) => (
                      <SliderControl
                        key={slider.id}
                        slider={slider}
                        index={index}
                        runView={runView}
                        onDeleteSlider={(sliderId) => void handleDeleteSlider(sliderId)}
                        onAutoMax={(sliderId) => void handleAutoMaxSlider(sliderId)}
                        canAutoMax={canAutoMax}
                        operation={operation}
                        disabled={!!slidersCaptured}
                      />
                    ))}
                  </div>
                )}
              </div>
            </Collapse>
          </div>
      </OffcanvasWrapper>
    </div>
  );
}

